import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { AccountSet } from "../redux/reducer/AccountSetting";
import { ValidateUser } from "../redux/reducer/ValidateUser";

// Bank Details Validation Schema
const bankDetailsValidationSchema = Yup.object().shape({
  bankName: Yup.string().required("Bank name is required"),
  accountName: Yup.string()
    .required("Account holder name is required")
    .min(3, "Account name must be at least 3 characters")
    .max(50, "Account name cannot exceed 50 characters"),
  accountNumber: Yup.string()
    .required("Account number is required")
    .min(10, "Account number must be at least 10 digits")
    .max(24, "Account number cannot exceed 24 characters")
    .matches(/^[0-9]+$/, "Account number must contain only digits"),
  ibanNumber: Yup.string()
    .max(24, "IBAN number cannot exceed 24 characters")
    .matches(/^[A-Z0-9]*$/, "IBAN must contain only uppercase letters and digits"),
});

// Pakistani banks list
const pakistaniBanks = [
  "Allied Bank Limited",
  "Askari Bank Limited",
  "Al Baraka Bank (Pakistan) Limited",
  "Bank Alfalah Limited",
  "Bank Al Habib Limited",
  "Bank Islami Pakistan Limited",
  "Dubai Islamic Bank Pakistan Limited",
  "Faysal Bank Limited",
  "First Women Bank Limited",
  "Habib Bank Limited",
  "Habib Metropolitan Bank Limited",
  "JS Bank Limited",
  "MCB Bank Limited",
  "MCB Islamic Bank Limited",
  "Meezan Bank Limited",
  "National Bank of Pakistan",
  "Samba Bank Limited",
  "Silk Bank Limited",
  "Sindh Bank Limited",
  "Soneri Bank Limited",
  "Standard Chartered Bank (Pakistan) Limited",
  "Summit Bank Limited",
  "The Bank of Khyber",
  "The Bank of Punjab",
  "United Bank Limited",
  "EasyPaisa",
  "JazzCash",
];

const BankDetailsModal = ({ open, onClose, onSuccess, tenantName, flatName, advanceAmount }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const formik = useFormik({
    initialValues: {
      bankName: "",
      accountName: "",
      accountNumber: "",
      ibanNumber: "",
    },
    validationSchema: bankDetailsValidationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setError("");

      try {
        const res = await dispatch(AccountSet({ values }));

        if (res?.payload?.data?.message === "Account Detail Save Successfully") {
          setSuccess(true);
          dispatch(ValidateUser({}));

          // Wait a moment to show success, then proceed
          setTimeout(() => {
            onSuccess();
          }, 1500);
        } else {
          setError(res?.payload?.data?.message || "Failed to save bank details");
        }
      } catch (err) {
        setError("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleClose = () => {
    if (!loading) {
      formik.resetForm();
      setError("");
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AccountBalanceIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Add Bank Details to Accept Request
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Info Box */}
        <Alert severity="info" sx={{ mb: 3 }}>
          {/* <Typography variant="body2">
            <strong>Why do we need this?</strong> Your bank details are required to receive the advance payment from the tenant.
            When <strong>{tenantName}</strong> pays the advance for <strong>{flatName}</strong>, the amount will be transferred to your account.
          </Typography> */}
          <Typography variant="body2">
            <strong>Why do we need this?</strong> Your bank details are required to receive the Rent, Advance or Monthly Bill payment from the tenant.
            When <strong>{tenantName}</strong> pays the bill for <strong>{flatName}</strong>, the amount will be transferred to your account.
          </Typography>
        </Alert>

        {/* Process Steps */}
        {/* <Box sx={{ mb: 3 }}>
          <Stepper activeStep={success ? 1 : 0} alternativeLabel>
            <Step completed={success}>
              <StepLabel>Add Bank Details</StepLabel>
            </Step>
            <Step completed={false}>
              <StepLabel>Tenant Pays Advance</StepLabel>
            </Step>
            <Step completed={false}>
              <StepLabel>Receive Payment</StepLabel>
            </Step>
          </Stepper>
        </Box> */}

        {success ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: "success.main", mb: 2 }} />
            <Typography variant="h6" color="success.main" gutterBottom>
              Bank Details Saved Successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Proceeding to accept the request...
            </Typography>
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <TextField
                select
                fullWidth
                label="Select Bank"
                name="bankName"
                value={formik.values.bankName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.bankName && Boolean(formik.errors.bankName)}
                helperText={formik.touched.bankName && formik.errors.bankName}
                disabled={loading}
              >
                <MenuItem value="">Select Bank</MenuItem>
                {pakistaniBanks.map((bank) => (
                  <MenuItem key={bank} value={bank}>
                    {bank}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                label="Account Holder Name"
                name="accountName"
                value={formik.values.accountName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.accountName && Boolean(formik.errors.accountName)}
                helperText={(formik.touched.accountName && formik.errors.accountName) || "Name as it appears on your bank account"}
                disabled={loading}
                inputProps={{ maxLength: 50 }}
              />

              <TextField
                fullWidth
                label="Account Number"
                name="accountNumber"
                value={formik.values.accountNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.accountNumber && Boolean(formik.errors.accountNumber)}
                helperText={(formik.touched.accountNumber && formik.errors.accountNumber) || "Your bank account number"}
                disabled={loading}
                inputProps={{ maxLength: 24 }}
              />

              <TextField
                fullWidth
                label="IBAN Number (Optional)"
                name="ibanNumber"
                value={formik.values.ibanNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.ibanNumber && Boolean(formik.errors.ibanNumber)}
                helperText={(formik.touched.ibanNumber && formik.errors.ibanNumber) || "International Bank Account Number (if available)"}
                disabled={loading}
                inputProps={{ maxLength: 24 }}
              />
            </Box>

            {/* Payment Info */}
            {advanceAmount && (
              <Alert severity="success" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>Expected Payment:</strong> Rs. {Number(advanceAmount).toLocaleString()}
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    (5% platform fee will be deducted from tenant's payment)
                  </Typography>
                </Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!success && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={formik.handleSubmit}
              disabled={loading || !formik.isValid}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? "Saving..." : "Save & Accept Request"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BankDetailsModal;
